import axios, { AxiosError } from 'axios';

import { routes } from '@/navigation/routes.tsx';
import { serverErrorService } from '@/services/ServerErrorService.ts';
import { checkAndRefreshToken } from '@/utils/check-and-refresh-token.ts';
import { apiUrls } from '@/api/api-urls.ts';
import { logError, logEvent } from '@/utils/sentry-monitor.tsx';
import { helpers } from '@/helpers.ts';
import {
  LIKE_ALREADY_EXISTS_ERR_CODE,
  LIKE_NOT_AUTHORISED_ERR_CODE,
  LIKES_LIMIT_REACHED
} from '@/constants/error_codes.ts';

export interface ServerError extends AxiosError {
  data: { code: number; msg: string } | string;
}

const baseURL = import.meta.env.VITE_API_BASE_URL;
const noAuthRoutes = [apiUrls.login.telegramConnect, routes.login];

export const ApiRefresh = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const Api = axios.create({
  baseURL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// The main interceptor for adding an authorization token
Api.interceptors.request.use(
  async (config) => {
    const requestId = (config.headers['X-Requested-With'] ||
      helpers.generateRequestId()) as string;

    // save requestId for using in response
    config.headers['X-Requested-With'] = requestId;

    if (noAuthRoutes.some((route) => config.url?.includes(route))) {
      return config;
    }

    // checkAndRefreshToken handles retries internally with proper error handling
    const token = await checkAndRefreshToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      logEvent(
        `[${requestId}] No valid token available for request`,
        { url: config.url },
        'warning',
        true
      );
    }

    return config;
  },
  (error: unknown) => {
    logError(error, { context: 'Request failed', error: error }, true);
    return Promise.reject(error);
  }
);

// Keep the original response interceptor for handling server errors
Api.interceptors.response.use(
  (response) => response,
  (error: ServerError) => {
    const requestId = error.config?.headers?.['X-Requested-With'] as string;

    const status = error.response?.status;
    const url = error.config?.url;

    if (!error.response) {
      logEvent(
        `[${requestId}] Network error`,
        {
          url,
          code: error.code,
          message: error.message
        },
        'warning',
        false
      );

      return Promise.reject(error);
    }

    if (status === 401 || status === 403) {
      logEvent(
        `[${requestId}] Auth error ${status}`,
        {
          url,
          hasAuthHeader: !!error.config?.headers?.Authorization,
          response: error.response.data
        },
        'warning',
        true
      );

      return Promise.reject(error);
    }

    if (status && status >= 400 && status < 500) {
      // Skip logging for known, expected error codes
      const responseData = error.response.data as { code?: number } | undefined;
      const errorCode = responseData?.code;
      const isExpectedError =
        errorCode === LIKE_ALREADY_EXISTS_ERR_CODE ||
        errorCode === LIKE_NOT_AUTHORISED_ERR_CODE ||
        errorCode === LIKES_LIMIT_REACHED;

      if (!isExpectedError) {
        logEvent(
          `[${requestId}] Client error ${status}`,
          {
            url,
            response: error.response.data
          },
          'info',
          true
        );
      }

      return Promise.reject(error);
    }

    if (status && status >= 500) {
      logError(
        error,
        {
          requestId,
          url,
          status,
          response: error.response?.data
        },
        true
      );

      serverErrorService.setError(error, 5000);
    }

    return Promise.reject(error);
  }
);

export default Api;
